// ── Auth API client ──────────────────────────────────────────────
// Session establishment goes through Next.js BFF `/api/auth/*`
// (HttpOnly cookies). User/profile calls go through `/api/proxy`.

import { ApiError, request } from "@/lib/api/client";

export { ApiError };

/** Token pair shape used only for one-shot `/api/auth/session` handoff. */
export interface TokenPair {
  accessToken: string;
  refreshToken: string;
  tokenType: "Bearer";
  /** Access token lifetime in seconds. */
  expiresIn: number;
}

export interface AuthIdentity {
  provider: "password" | "google" | "apple";
  identifier: string | null;
}

export interface SubscriptionLimit {
  key: string;
  used: number;
  max: number;
  period: string;
}

export interface SubscriptionFeature {
  key: string;
  value: number;
}

export interface UserSubscriptionSummary {
  isActive: boolean;
  isCancelled: boolean;
  cancellable: boolean;
  planSlug: string;
  planName: string;
  startsAt: string;
  endsAt: string;
  limits: SubscriptionLimit[];
  features: SubscriptionFeature[];
}

export interface AuthUser {
  id: number;
  firstName: string | null;
  lastName: string | null;
  registeredAt: string;
  lastVisitedAt: string | null;
  roles: string[];
  isEmailVerified: boolean;
  identities: AuthIdentity[];
  subscription: UserSubscriptionSummary | null;
}

/** Establish session cookies via BFF (no tokens returned to JS). */
async function establishViaBff(path: string, body: unknown): Promise<void> {
  await request<unknown>(path, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function authenticate(email: string, password: string): Promise<void> {
  return establishViaBff("/api/auth/login", { email, password });
}

export function register(email: string, password: string): Promise<void> {
  return establishViaBff("/api/auth/register", { email, password });
}

/** Resets the password and emails the new one (204 even when the email is unknown). */
export function recoverPassword(email: string): Promise<void> {
  return request<void>("/v1/authentication/password/recover", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

/** Resends the confirmation email for an unverified password identity (204). */
export function resendEmailConfirmation(email: string): Promise<void> {
  return request<void>("/v1/authentication/email/confirmation/resend", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

/** Confirms a password identity email with the one-time token from the link (204). */
export function confirmEmail(token: string): Promise<void> {
  return request<void>("/v1/authentication/email/confirm", {
    method: "POST",
    body: JSON.stringify({ token }),
  });
}

/**
 * Consume a one-time magic login-link token
 * (`POST /v1/authentication/login-link/consume` via BFF).
 * Parks JWTs into HttpOnly cookies; no Authorization header.
 */
export function consumeLoginLink(token: string): Promise<void> {
  return establishViaBff("/api/auth/login-link", { token });
}

/** Exchanges a Google-issued id token for a Nurse Tools cookie session. */
export function oauthGoogle(idToken: string): Promise<void> {
  return establishViaBff("/api/auth/oauth/google", { idToken });
}

/** Exchanges an Apple-issued id token for a Nurse Tools cookie session. */
export function oauthApple(idToken: string): Promise<void> {
  return establishViaBff("/api/auth/oauth/apple", { idToken });
}

/**
 * Park an already-issued token pair into HttpOnly cookies
 * (e.g. legacy handoff). Prefer proxy-sanitized checkout complete.
 */
export function acceptTokenPair(pair: TokenPair): Promise<void> {
  return establishViaBff("/api/auth/session", {
    accessToken: pair.accessToken,
    refreshToken: pair.refreshToken,
    expiresIn: pair.expiresIn,
  });
}

/**
 * Concurrent callers share one request. React Strict Mode mounts the provider
 * twice on boot, and screens can adopt the session while it is still resolving
 * — both would otherwise issue their own `/v1/user/me`.
 */
let inflightMe: Promise<AuthUser> | null = null;

export function fetchMe(): Promise<AuthUser> {
  if (inflightMe) return inflightMe;
  const pending = request<AuthUser>("/v1/user/me", {
    method: "GET",
  }).finally(() => {
    if (inflightMe === pending) inflightMe = null;
  });
  inflightMe = pending;
  return pending;
}

export interface MeUpdatePayload {
  firstName?: string | null;
  lastName?: string | null;
}

/** Updates the current user's profile (`PATCH /v1/user/me`). */
export function updateMe(payload: MeUpdatePayload): Promise<AuthUser> {
  return request<AuthUser>("/v1/user/me", {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function logoutRequest(): Promise<void> {
  await request<void>("/api/auth/logout", {
    method: "POST",
  });
}
